import { formatDistanceToNow } from 'date-fns'
import { ExternalLink, Plug, RefreshCw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import type { WpConnection } from '@/api/hooks/use-wordpress'
import {
  useDeleteWpArticle,
  useRevokeWpConnection,
  useWpArticles,
  useWpConnections,
} from '@/api/hooks/use-wordpress'
import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import { WP_PLUGIN_DOWNLOAD_URL } from '@/lib/wp-plugin-url'
import { CreateWpTokenModal } from '@/pages/wordpress-create-token-modal'
import { WordPressGroupsSection } from '@/pages/wordpress-groups'

export function WordPressPage(): React.JSX.Element {
  const { data: connectionsData, isLoading: loadingConns } = useWpConnections()
  const revokeConnection = useRevokeWpConnection()
  const { data: articlesData, isLoading: loadingArticles } = useWpArticles()
  const deleteArticle = useDeleteWpArticle()

  const [showCreate, setShowCreate] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; name: string } | null>(null)

  const connections = connectionsData?.data ?? []
  const articles = articlesData?.data ?? []

  function handleRevoke(): void {
    if (!revokeTarget) return
    revokeConnection.mutate(revokeTarget.id, { onSuccess: () => setRevokeTarget(null) })
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">WordPress Integration</h1>
        <div className="flex items-center gap-2">
          <a href={WP_PLUGIN_DOWNLOAD_URL} download className="rounded-lg border border-surface-3 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1">
            Download Plugin
          </a>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            + Connect Site
          </button>
        </div>
      </div>

      {/* Connections */}
      <section className="space-y-3">
        <h2 className="text-lg font-medium text-text-primary">Connected Sites</h2>
        {loadingConns ? (
          <p className="text-sm text-text-tertiary">Loading connections...</p>
        ) : connections.length === 0 ? (
          <EmptyConnections onConnect={() => setShowCreate(true)} />
        ) : (
          <ConnectionsList
            connections={connections}
            onRevoke={(c) => setRevokeTarget({ id: c.id, name: c.name })}
          />
        )}
      </section>

      {/* Article Groups */}
      {articles.length > 0 && <WordPressGroupsSection articles={articles} />}

      {/* Articles */}
      {articles.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-medium text-text-primary">Synced Articles</h2>
          {loadingArticles ? (
            <p className="text-sm text-text-tertiary">Loading articles...</p>
          ) : (
            <DataTable
              columns={[
                {
                  key: 'title',
                  label: 'Title',
                  render: (r) => (
                    <div className="flex items-center gap-2">
                      {r.cover_image_url && (
                        <img
                          src={r.cover_image_url}
                          alt=""
                          className="h-8 w-8 rounded object-cover"
                        />
                      )}
                      <span className="font-medium">{r.title}</span>
                    </div>
                  ),
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (r) => (
                    <StatusBadge status={r.status === 'published' ? 'active' : 'pending'}>
                      {r.status}
                    </StatusBadge>
                  ),
                },
                {
                  key: 'author',
                  label: 'Author',
                  render: (r) => <span className="text-text-secondary">{r.author ?? '—'}</span>,
                },
                {
                  key: 'synced_at',
                  label: 'Synced',
                  render: (r) => (
                    <span className="text-text-secondary">
                      {formatDistanceToNow(new Date(r.synced_at), { addSuffix: true })}
                    </span>
                  ),
                },
                {
                  key: 'actions',
                  label: '',
                  render: (r) => (
                    <div className="flex gap-2">
                      {r.permalink && (
                        <a
                          href={r.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded p-1 text-text-tertiary hover:text-text-primary"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteArticle.mutate(r.id)
                        }}
                        className="rounded p-1 text-text-tertiary hover:text-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ),
                },
              ]}
              data={articles}
            />
          )}
        </section>
      )}

      {showCreate && <CreateWpTokenModal onClose={() => setShowCreate(false)} />}

      {revokeTarget && (
        <RevokeModal
          name={revokeTarget.name}
          isPending={revokeConnection.isPending}
          onConfirm={handleRevoke}
          onCancel={() => setRevokeTarget(null)}
        />
      )}
    </div>
  )
}

function EmptyConnections({ onConnect }: { onConnect: () => void }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-surface-3 py-12 text-center">
      <Plug className="mb-4 h-12 w-12 text-text-tertiary" />
      <h3 className="text-lg font-medium text-text-primary">No WordPress sites connected</h3>
      <p className="mt-1 max-w-sm text-sm text-text-secondary">
        Connect your WordPress site to import articles directly into your newsletters.
      </p>
      <button
        type="button"
        onClick={onConnect}
        className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        Connect your first site
      </button>
    </div>
  )
}

function ConnectionsList({
  connections,
  onRevoke,
}: {
  connections: WpConnection[]
  onRevoke: (c: WpConnection) => void
}): React.JSX.Element {
  return (
    <div className="space-y-3">
      {connections.map((c) => (
        <div
          key={c.id}
          className="flex items-center justify-between rounded-lg border border-surface-3 bg-surface-0 p-4"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50">
              <Plug className="h-5 w-5 text-brand-600" />
            </div>
            <div>
              <p className="font-medium text-text-primary">{c.name}</p>
              <p className="text-sm text-text-secondary">
                {c.site_url ?? `Token: ${c.token_hint}`}
                {c.last_sync_at && (
                  <span className="ml-2">
                    <RefreshCw className="mr-1 inline h-3 w-3" />
                    {formatDistanceToNow(new Date(c.last_sync_at), { addSuffix: true })}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge
              status={
                c.status === 'connected' ? 'active' : c.status === 'expired' ? 'failed' : 'pending'
              }
            >
              {c.status}
            </StatusBadge>
            <button
              type="button"
              onClick={() => onRevoke(c)}
              className="rounded-md px-3 py-1 text-sm text-danger hover:bg-red-50"
            >
              Revoke
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

function RevokeModal({
  name,
  isPending,
  onConfirm,
  onCancel,
}: {
  name: string
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}): React.JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-surface-0 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-text-primary">Revoke Connection</h3>
        <p className="mt-2 text-sm text-text-secondary">
          Revoking &ldquo;{name}&rdquo; will disconnect the WordPress site and delete its synced
          articles.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
          >
            {isPending ? 'Revoking...' : 'Revoke'}
          </button>
        </div>
      </div>
    </div>
  )
}
